import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Banknote, Link2, CheckCircle2 } from "lucide-react";

const statusColors = { pending: "bg-amber-50 text-amber-700 border-amber-200", completed: "bg-emerald-50 text-emerald-700 border-emerald-200", rejected: "bg-red-50 text-red-700 border-red-200" };

export const WithdrawCard = ({ outstanding, claimed = 0, onChanged }) => {
  const [connect, setConnect] = useState(null);
  const [requests, setRequests] = useState([]);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/connect/status").then((r) => setConnect(r.data)).catch(() => {});
    api.get("/payouts/my-requests").then((r) => setRequests(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("connect") === "done") {
      toast.success("Stripe account setup complete!");
      setParams({});
      load();
    }
  }, [params, setParams, load]);

  const startOnboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(fmtErr(e)); setBusy(false); }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.post("/payouts/request", { amount: amount ? parseFloat(amount) : null });
      toast.success("Withdrawal requested — our team will process it shortly");
      setAmount("");
      load();
      onChanged?.();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const pending = requests.some((r) => r.status === "pending");

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-8" data-testid="withdraw-card">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex-1 min-w-[260px]">
          <h3 className="font-semibold font-heading flex items-center gap-2"><Banknote size={18} /> Withdraw earnings</h3>
          <p className="text-sm text-neutral-500 mt-1">
            Available balance: <span className="font-semibold text-emerald-700" data-testid="withdraw-available">${(outstanding ?? 0).toFixed(2)}</span>
          </p>
          {claimed > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mt-2 inline-block" data-testid="withdraw-claimed-note">
              ${claimed.toFixed(2)} already claimed — awaiting payout, not included above
            </p>
          )}
          {connect && (connect.connected && connect.payouts_enabled ? (
            <p data-testid="connect-status-connected" className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mt-3">
              <CheckCircle2 size={13} /> Stripe account connected — withdrawals transfer automatically
            </p>
          ) : (
            <div className="mt-3">
              <p className="text-xs text-neutral-500 mb-2">
                {connect.configured
                  ? connect.connected
                    ? "Your Stripe account setup is incomplete — finish onboarding to receive automatic transfers."
                    : "Connect your Stripe account to receive withdrawals directly. Without it, our team pays you manually."
                  : "Automatic Stripe transfers aren't enabled on the platform yet — withdrawals are processed manually by our team."}
              </p>
              {connect.configured && (
                <button data-testid="connect-stripe-button" onClick={startOnboard} disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">
                  <Link2 size={14} /> {connect.connected ? "Finish Stripe setup" : "Connect Stripe account"}
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs font-medium text-neutral-500">Amount (blank = full balance)</label>
            <input data-testid="withdraw-amount-input" type="number" step="0.01" min="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder={(outstanding ?? 0).toFixed(2)}
              className="mt-1 w-40 bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none" />
          </div>
          <button data-testid="withdraw-request-button" onClick={withdraw}
            disabled={busy || pending || !outstanding || outstanding <= 0}
            className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-40">
            {pending ? "Request pending" : "Request withdrawal"}
          </button>
        </div>
      </div>
      {requests.length > 0 && (
        <div className="mt-5 border-t border-neutral-100 pt-4 space-y-2" data-testid="withdraw-requests-list">
          {requests.slice(0, 4).map((r) => (
            <div key={r.id} className="flex items-center gap-3 text-sm">
              <span className={`text-xs font-medium border rounded-full px-2.5 py-0.5 ${statusColors[r.status] || "bg-neutral-50 text-neutral-600 border-neutral-200"}`}>{r.status}</span>
              <span className="font-semibold">${r.amount.toFixed(2)}</span>
              <span className="text-neutral-400 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
